<script lang="ts">
  import Icon from './Icon.svelte';

  let {
    value,
    editable = false,
    ariaLabel,
    onCommit,
    headingClass = '',
    as = 'h2',
    class: cls = '',
  }: {
    value: string;
    editable?: boolean;
    ariaLabel: string;
    onCommit: (next: string) => Promise<void>;
    headingClass?: string;
    as?: 'h1' | 'h2' | 'h3';
    class?: string;
  } = $props();

  let el: HTMLElement | null = $state(null);
  let saving = $state(false);

  // Keep the contenteditable's DOM text in sync with the `value` prop.
  // Without this, an external update (e.g., another save path) would
  // be silently masked by whatever text is in the contenteditable.
  $effect(() => {
    void value;
    if (!el) return;
    if (saving) return;
    if (el.textContent !== value) el.textContent = value;
  });

  async function onBlur() {
    if (!editable || !el || saving) return;
    const next = (el.textContent ?? '').trim();
    if (next === '' || next === value) {
      el.textContent = value;
      return;
    }
    saving = true;
    el.textContent = next;
    try {
      await onCommit(next);
    } catch (e) {
      console.error('EditableTitle commit failed:', e);
      if (el) el.textContent = value;
    } finally {
      saving = false;
    }
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      el?.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (el) el.textContent = value;
      el?.blur();
    }
  }
</script>

<div class="editable-title {cls}">
  <svelte:element
    this={as}
    bind:this={el}
    class="heading {headingClass}"
    class:editable
    contenteditable={editable}
    spellcheck="false"
    role="heading"
    aria-level={as === 'h1' ? 1 : as === 'h2' ? 2 : 3}
    aria-label={ariaLabel}
    onblur={onBlur}
    onkeydown={onKeydown}
  >{value}</svelte:element>
  {#if editable}
    <button
      type="button"
      class="pencil"
      aria-label="Rename"
      title="Rename"
      onclick={() => el?.focus()}
    >
      <Icon name="pencil" size={14} strokeWidth={2} />
    </button>
  {/if}
</div>

<style>
  /* Flex (not inline-flex) + no flex-wrap so the pencil stays on the
     same row even when the heading text wraps to multiple lines.
     `flex: 0 1 auto` on the heading lets it sit at content width when
     it fits, and shrink (with min-width: 0) to wrap text when the
     container is too narrow. align-items: flex-start parks the pencil
     next to the first line of a wrapping heading. */
  .editable-title {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
  }
  .heading {
    flex: 0 1 auto;
    min-width: 0;
    border-radius: var(--radius-xs);
    padding: 2px 4px;
    margin: -2px -4px;
    border: 1px solid transparent;
    outline: none;
  }
  .heading.editable { cursor: text; }
  .heading.editable:hover { background: var(--color-surface-soft); }
  .heading.editable:focus {
    background: var(--color-surface-soft);
    border-color: var(--color-hairline);
  }
  .pencil {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--color-muted);
    border: 0;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    cursor: pointer;
    flex: none;
    /* Nudges the pencil down 2px so its center aligns with the first
       line of the heading text (heading has 2px top padding). */
    margin-top: 2px;
    transition: background 100ms ease, color 100ms ease;
  }
  .pencil:hover,
  .pencil:focus-visible {
    background: var(--color-surface-soft);
    color: var(--color-ink);
    outline: none;
  }
  .heading:focus + .pencil { display: none; }
</style>
